/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback, useState, useReducer, Fragment, useRef } from "react";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { useFlags } from "launchdarkly-react-client-sdk";
import axios from "axios";
import classNames from "classnames";
import utils from "@utils";
import { isEqual } from "@utils/lodash";
import { NON_RKPAY_INVOICE_TYPE, INVOICE_TYPE_FILTER } from "@constants";
import {
	ClientService,
	ProjectService,
	LocationService,
	ContactService,
	OfficerService,
	OrganizationService,
} from "@services";
import { Select, Chip, DateRange } from "@components/common";
import { Toogle, SearchOfficerModal } from "../../../components";
import { FEATURES } from "../../../js/constants";

const DEFAULT_FILTERS = {
	clientId: null,
	organizationId: null,
	projectId: null,
	locationId: null,
	officerId: null,
	startDate: null,
	endDate: null,
	organizationIds: null,
	startCreateDate: null,
	endCreateDate: null,
	vehicleInvoicingRequired: null,
	prepaid: null,
	onlyVoided: null,
	supervisorIds: null,
	paid: null,
	onlyOverpaid: null,
	archived: null,
	pendingOfficerSubmission: null,
	disputed: null,
	invoiceType: null,
};

const initialRecords = {
	clients: new Map(),
	projects: new Map(),
	locations: new Map(),
	coordinators: new Map(),
	organizations: new Map(),
};

const RECORDS_ACTIONS = {
	SET_RECORDS: "SET_RECORDS",
	PATCH_RECORDS: "PATCH_RECORDS",
	RESET_RECORDS: "RESET_RECORDS",
};

const recordsReducer = (state, action) => {
	switch (action.type) {
		case RECORDS_ACTIONS.SET_RECORDS:
			return {
				...state,
				[action.payload.key]: action.payload.value,
			};
		case RECORDS_ACTIONS.PATCH_RECORDS:
			return {
				...state,
				...action.payload,
			};
		case RECORDS_ACTIONS.RESET_RECORDS:
			return initialRecords;
		default:
			return state;
	}
};

const NonRKPayInvoiceFilters = ({
	user,
	activeFilters,
	activeType,
	onFiltersChange,
	overpaidInvoicesCount,
	pendingSubmissionByOfficerCount,
}) => {
	const { lang } = global;

	const [showCalendar, setShowCalendar] = useState(false);
	const [showCreateDateCalendar, setShowCreateDateCalendar] = useState(false);

	const [loading, setLoading] = useState({
		clients: false,
		projects: false,
		locations: false,
		coordinators: false,
		organizations: false,
	});
	const [records, dispatchRecords] = useReducer(recordsReducer, initialRecords);
	const [currentFilters, setCurrentFilters] = useState({ ...DEFAULT_FILTERS, ...activeFilters });
	const [showSearchOfficerModal, setShowSearchOfficerModal] = useState(false);
	const [selectedOfficer, setSelectedOfficer] = useState(null);
	const coordinatorMultiselectRef = useRef(null);
	const [selectedCoordinators, setSelectedCoordinators] = useState([]);
	const [showFilters, setShowFilters] = useState(false);

	const { onlyVoidedToggleEnabled, clientV4InvoicingDisputes } = useFlags();

	const isAdminOrOfficerInvoicingV3 =
		user.isSuperAdmin() ||
		user.Features.some(f => f.toLowerCase() === FEATURES.OFFICER_INVOICING_V3);

	const isOfficerInvoicingV3 = user.Features.some(f => f.toLowerCase() === FEATURES.OFFICER_INVOICING_V3);

	const setRecords = (key, value) => {
		dispatchRecords({ type: RECORDS_ACTIONS.SET_RECORDS, payload: { key, value } });
	};

	const displayErrorMessage = (error, message) =>
		utils.notify({
			type: "error",
			message: error.response?.data?.Message ?? message,
			timeOut: 1500,
		});

	const paidFilterOptions = [
		{ value: true, label: lang.t("Paid") },
		{ value: false, label: lang.t("Unpaid") },
	];

	const invoiceTypeOptions = [
		{ value: INVOICE_TYPE_FILTER.AGENCY, label: lang.t("Agency") },
		{ value: INVOICE_TYPE_FILTER.OFFICER, label: lang.t("Officer") },
		{ value: INVOICE_TYPE_FILTER.MANUAL, label: lang.t("Manual") },
	];

	const loadClients = async () => {
		try {
			setLoading({ ...loading, clients: true });
			const params = {
				includeInactive: false,
			};
			if (user.isSuperAdmin() && currentFilters.organizationId) {
				params.organizationId = currentFilters.organizationId;
			}
			const clients = await ClientService.getAll(params).then(({ data }) => {
				const clientsMap = new Map();
				data.map(({ Id, Name }) =>
					clientsMap.set(Id.toString(), { value: Id.toString(), label: Name }),
				);

				return clientsMap;
			});

			setRecords("clients", clients);
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading clients"));
			}
		} finally {
			setLoading({ ...loading, clients: false });
		}
	};

	const loadProjects = async () => {
		try {
			setLoading({ ...loading, projects: true });
			const projects = await ProjectService.getAll({
				includeAll: false,
				clientId: currentFilters.clientId,
			}).then(({ data }) => {
				const projectsMap = new Map();
				data.ItemList.map(({ Id, Name }) =>
					projectsMap.set(Id.toString(), { value: Id.toString(), label: Name }),
				);

				return projectsMap;
			});

			setRecords("projects", projects);
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading projects"));
			}
		} finally {
			setLoading({ ...loading, projects: false });
		}
	};

	const loadLocations = async () => {
		try {
			setLoading({ ...loading, locations: true });

			const locations = await LocationService.getAll({
				minimalData: true,
				includeDeleted: false,
				projectId: currentFilters.projectId,
				clientId: currentFilters.clientId,
			}).then(({ data }) => {
				const locationsMap = new Map();
				data.map(({ Id, Name }) =>
					locationsMap.set(Id.toString(), { value: Id.toString(), label: Name }),
				);

				return locationsMap;
			});

			setRecords("locations", locations);
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading Locations"));
			}
		} finally {
			setLoading({ ...loading, locations: false });
		}
	};

	const loadCoordinators = async () => {
		try {
			setLoading({ ...loading, coordinators: true });
			const orgId = user.isSuperAdmin()
				? currentFilters.organizationId
				: user?.Organization?.Id;
			if (orgId) {
				const coordinators = await ContactService.getAll({
					clientId: orgId,
					isOrganization: true,
				}).then(({ data }) => {
					const coordinatorsMap = new Map();
					data.ItemList.map(({ Id, ContactName }) =>
						coordinatorsMap.set(Id.toString(), {
							value: Id.toString(),
							label: ContactName,
						}),
					);
					// Set active supervisor filters
					if (currentFilters.supervisorIds) {
						const supervisorIds = Array.isArray(currentFilters.supervisorIds)
							? currentFilters.supervisorIds
							: [currentFilters.supervisorIds];
						const selectedOptions = [];
						supervisorIds.forEach(id => {
							const coordinator = coordinatorsMap.get(id);
							if (coordinator) {
								selectedOptions.push(coordinator);
							}
						});
						setSelectedCoordinators(selectedOptions);
					}

					return coordinatorsMap;
				});

				setRecords("coordinators", coordinators);
			}
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading Coordinators"));
			}
		} finally {
			setLoading({ ...loading, coordinators: false });
		}
	};

	const loadOfficer = async id => {
		try {
			await OfficerService.getById(id).then(({ data }) => {
				setSelectedOfficer(data.ContactViewModel);
			});
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading officer"));
			}
		}
	};

	const loadOrganizations = async () => {
		try {
			setLoading({ ...loading, organizations: true });
			const organizations = await OrganizationService.getAll({ minimalData: true });
			const organizationsMap = new Map();
			organizations.data?.map(({ Id, Name }) =>
				organizationsMap.set(Id.toString(), { value: Id.toString(), label: Name }),
			);
			setRecords("organizations", organizationsMap);
		} catch (error) {
			if (!axios.isCancel(error)) {
				displayErrorMessage(error, lang.t("Error loading organizations"));
			}
		} finally {
			setLoading({ ...loading, organizations: false });
		}
	};

	const loadFilters = useCallback(async () => {
		if (user.isSuperAdmin()) {
			await loadOrganizations();
		}
		await loadClients();
		await loadCoordinators();

		if (activeFilters && activeFilters.officerId) {
			await loadOfficer(activeFilters.officerId);
		}
	}, []);

	const onJobDateFilter = () => setShowCalendar(true);

	const onCreateDateFilter = () => setShowCreateDateCalendar(true);

	const clearAllCoordinatorMultiselect = () => {
		const targetRef = coordinatorMultiselectRef.current;
		if (targetRef) {
			targetRef.setState({
				value: [],
			});
		}
	};

	const clearAllFilters = () => {
		// clear filters but keep the sorting
		const defaultFilters = {
			...DEFAULT_FILTERS,
			sortBy: currentFilters.sortBy,
			asc: currentFilters.asc,
			vehicleInvoicingRequired: currentFilters.vehicleInvoicingRequired,
			onlyOverpaid: currentFilters.onlyOverpaid,
			archived: currentFilters.archived,
			pendingOfficerSubmission: currentFilters.pendingOfficerSubmission,
			disputed: currentFilters.disputed,
		};

		setCurrentFilters(defaultFilters);
		setSelectedCoordinators([]);
		setSelectedOfficer(null);
		clearAllCoordinatorMultiselect();
	};

	const resetClientFilter = () => {
		setCurrentFilters({
			...currentFilters,
			clientId: null,
			projectId: null,
			locationId: null,
		});
	};

	const resetProjectFilter = () => {
		setCurrentFilters({
			...currentFilters,
			projectId: null,
			locationId: null,
		});
	};

	const resetOrganizationFilter = () => {
		dispatchRecords({
			type: RECORDS_ACTIONS.PATCH_RECORDS,
			payload: {
				projects: [],
				locations: [],
				clients: [],
				coordinators: [],
			},
		});
		setCurrentFilters({
			...currentFilters,
			organizationId: null,
			clientId: null,
			projectId: null,
			locationId: null,
			supervisorIds: null,
		});
	};

	const resetCoordinatorFilterMultiselect = id => {
		const updatedSupervisorIds = currentFilters.supervisorIds.filter(o => o !== id);
		setCurrentFilters({
			...currentFilters,
			supervisorIds: updatedSupervisorIds.length > 0 ? updatedSupervisorIds : null,
		});

		const newSelectedOptions = selectedCoordinators.filter(coo => coo.value !== id);
		setSelectedCoordinators(newSelectedOptions);

		const newMultiSelectOptions = coordinatorMultiselectRef.current.state.value.filter(
			ms => ms.value !== id,
		);
		coordinatorMultiselectRef.current.setState({
			value: newMultiSelectOptions,
		});
	};

	const openSearchOfficerModal = () => setShowSearchOfficerModal(true);

	const handleOfficerChange = officer => {
		setCurrentFilters({ ...currentFilters, officerId: officer.Id });
		setSelectedOfficer(officer);
	};

	const onChangeCoordinatorHandler = selectedOptions => {
		setCurrentFilters({
			...currentFilters,
			supervisorIds: selectedOptions.map(item => item.value),
		});
		setSelectedCoordinators(selectedOptions);
	};

	const showClearAllFilters = () => {
		const {
			sortBy,
			asc,
			vehicleInvoicingRequired,
			onlyOverpaid,
			archived,
			pendingOfficerSubmission,
			disputed,
			...tempFilters
		} = currentFilters;

		return Object.values(tempFilters).filter(filter => filter !== null).length > 0;
	};

	useEffect(() => {
		if (currentFilters.projectId && currentFilters.clientId) {
			loadLocations();
			return;
		}

		setCurrentFilters({ ...currentFilters, locationId: null });
		setRecords({ ...records, locations: [] });
	}, [currentFilters.projectId, currentFilters.clientId]);

	useEffect(() => {
		if (currentFilters.clientId) {
			loadProjects();
			return;
		}

		setCurrentFilters({
			...currentFilters,
			projectId: null,
			locationId: null,
		});

		dispatchRecords({
			type: RECORDS_ACTIONS.PATCH_RECORDS,
			payload: {
				projects: [],
				locations: [],
			},
		});
	}, [currentFilters.clientId]);

	useEffect(() => {
		if (currentFilters.organizationId) {
			loadClients();
			loadCoordinators();
			return;
		}
		dispatchRecords({
			type: RECORDS_ACTIONS.PATCH_RECORDS,
			payload: {
				clients: [],
				projects: [],
				locations: [],
				coordinators: [],
			},
		});
	}, [currentFilters.organizationId]);

	useEffect(() => {
		const filters = {};
		const keyFilters = Object.keys(currentFilters);
		keyFilters.forEach(key => {
			if (currentFilters[key] !== null) {
				filters[key] = currentFilters[key];
			}
		});

		// Check if filters have actually changed
		if (!isEqual(activeFilters, filters)) {
			onFiltersChange(filters);
		}
	}, [JSON.stringify(currentFilters)]);

	useEffect(() => {
		loadFilters();
	}, []);
	useEffect(() => {
		setCurrentFilters({ ...DEFAULT_FILTERS, ...activeFilters });
	}, [activeFilters]);
	return (
		<Fragment>
			<div className="row invoice-filters margin-bottom-24">
				<div className="col-xs-12 flex flex-wrap filters-container">
					<h4 className="hidden-xs font-bold margin-right-8">{lang.t("Filter by")} : </h4>
					<div className="hidden-sm hidden-md hidden-lg flex flex-align-items-center flex-justify-content-space-between">
						<button
							type="button"
							className="btn btn-link filters__toggle-button"
							onClick={() => setShowFilters(prevState => !prevState)}
						>
							<span className="h4 font-semi-bold margin-right-12">
								{lang.t("Filters")}
							</span>
							<i
								className={`text-primary glyphicon glyphicon-chevron-${showFilters ? "up" : "down"}`}
							/>
						</button>
						<label htmlFor="selectAll">
							<span className="font-semi-bold margin-right-12">
								{lang.t("Select All")}
							</span>
							<input
								type="checkbox"
								name="selectAll"
								id="selectAll"
								title={lang.t("Select All")}
								aria-label="Select All Invoices"
								disabled
							/>
						</label>
					</div>
					<div className="filter-options-container">
						<If condition={user.isSuperAdmin()}>
							<Select
								className={`invoice-filters__select width-min-120 ${showFilters ? "" : "hidden-xs"}`}
								placeholder={lang.t("Agency")}
								onChange={organization => {
									if (organization && organization.value) {
										setCurrentFilters({
											...currentFilters,
											organizationId: organization.value,
										});
									}
								}}
								options={[...records.organizations.values()]}
								isLoading={loading.organizations}
								isDisabled={currentFilters.organizationId !== null}
								isClearable
								data-test-id="invoice-filters__select-agency"
							/>
						</If>
						<div data-test-id="invoice-filters__select-client-container">
							<Select
								className={classNames('invoice-filters__select width-min-120', { disabled: user.isSuperAdmin() && !currentFilters.organizationId }, { 'hidden-xs': !showFilters })}
								placeholder={lang.t('Client')}
								onChange={(client) => { if (client && client.value) { setCurrentFilters({ ...currentFilters, clientId: client.value }) } }}
								options={[...records.clients.values()]}
								isLoading={loading.clients}
								isDisabled={currentFilters.clientId !== null || (user.isSuperAdmin() && !currentFilters.organizationId)}
								isClearable
								data-test-id="invoice-filters__select-client"
							/>
						</div>

						<Select
							className={classNames(
								"invoice-filters__select width-min-120",
								{ disabled: !currentFilters.clientId },
								{ "hidden-xs": !showFilters },
							)}
							placeholder={lang.t("Project")}
							onChange={project => {
								if (project && project.value) {
									setCurrentFilters({
										...currentFilters,
										projectId: project.value,
									});
								}
							}}
							options={[...records.projects.values()]}
							isLoading={loading.projects}
							isDisabled={
								currentFilters.projectId !== null || !currentFilters.clientId
							}
							isClearable
							data-test-id="invoice-filters__select-project"
						/>

						<Select
							className={classNames(
								"invoice-filters__select width-min-120",
								{ disabled: !currentFilters.projectId },
								{ "hidden-xs": !showFilters },
							)}
							placeholder={lang.t("Location")}
							onChange={location => {
								if (location && location.value) {
									setCurrentFilters({
										...currentFilters,
										locationId: location.value,
									});
								}
							}}
							options={[...records.locations.values()]}
							isLoading={loading.locations}
							isDisabled={
								currentFilters.locationId !== null || !currentFilters.projectId
							}
							isClearable
							data-test-id="invoice-filters__select-location"
						/>
						<If condition={activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key}>
							<button
								type="button"
								className={`btn btn-primary invoice-filters__button ${showFilters ? "" : "hidden-xs"}`}
								onClick={openSearchOfficerModal}
								data-test-id="invoice-filters__select-officer"
							>
								{lang.t("Officers")}
							</button>
						</If>

						<button
							type="button"
							className={`btn btn-primary invoice-filters__button ${showFilters ? "" : "hidden-xs"}`}
							onClick={onJobDateFilter}
							data-test-id="invoice-filters__select-job-date"
						>
							{lang.t("Job Date")}
						</button>

						<If condition={activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key}>
							<button
								type="button"
								className={`btn btn-primary invoice-filters__button ${showFilters ? "" : "hidden-xs"}`}
								onClick={onCreateDateFilter}
								data-test-id="invoice-filters__select-created-date"
							>
								{lang.t("Created Date")}
							</button>
						</If>

						<ReactSelect
							className={classNames(
								"invoice-filters__select width-min-120",
								{ disabled: user.isSuperAdmin() && !currentFilters.organizationId },
								{ "hidden-xs": !showFilters },
							)}
							classNamePrefix="react-select"
							placeholder={lang.t("Coordinator")}
							name="coordinator-multiselect"
							ref={coordinatorMultiselectRef}
							value={selectedCoordinators}
							onChange={selectedOptions => {
								onChangeCoordinatorHandler(selectedOptions);
							}}
							options={[...records.coordinators.values()]}
							isLoading={loading.coordinators}
							isClearable={false}
							isMulti
							closeMenuOnSelect
							controlShouldRenderValue={false}
							isDisabled={user.isSuperAdmin() && !currentFilters.organizationId}
							data-test-id="invoice-filters__select-coordinator"
						/>

						<If condition={activeType === NON_RKPAY_INVOICE_TYPE.ESTIMATES.key}>
							<Select
								className={classNames("invoice-filters__select width-min-120", {
									"hidden-xs": !showFilters,
								})}
								placeholder={lang.t("Paid Status")}
								onChange={status => {
									if (status) {
										setCurrentFilters({
											...currentFilters,
											paid: status?.value,
										});
									}
								}}
								options={paidFilterOptions}
								isDisabled={currentFilters.paid !== null}
								isClearable
								data-test-id="invoice-filters__select-paid-status"
							/>
						</If>

						<If condition={activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key && isOfficerInvoicingV3}>
							<Select
								className={classNames("invoice-filters__select width-min-120", {
									"hidden-xs": !showFilters,
								})}
								placeholder={lang.t("Invoice Type")}
								onChange={invoiceType => {
									if (invoiceType) {
										setCurrentFilters({
											...currentFilters,
											invoiceType: invoiceType?.value,
										});
									}
								}}
								options={invoiceTypeOptions}
								isDisabled={currentFilters.invoiceType !== null}
								isClearable
								data-test-id="invoice-filters__select-invoice-type"
							/>
						</If>
					</div>
					<div className={`col-xs-12 divider ${showFilters ? "" : "hidden-xs"}`} />

					<div
						className={`col-xs-12 flex flex-wrap filters__applied applied-filters-container ${showFilters ? "" : "hidden-xs"}`}
					>
						<If condition={currentFilters.organizationId}>
							<Chip
								label={`Agency: ${(records.organizations.get && records.organizations.get(currentFilters.organizationId)?.label) || ""}`}
								onDelete={() => resetOrganizationFilter()}
							/>
						</If>
						<If condition={currentFilters.clientId}>
							<Chip
								label={`Client: ${(records.clients.get && records.clients.get(currentFilters.clientId)?.label) || ""}`}
								onDelete={() => resetClientFilter()}
							/>
						</If>

						<If condition={currentFilters.projectId}>
							<Chip
								label={`Project: ${(records.projects.get && records.projects.get(currentFilters.projectId)?.label) || ""}`}
								onDelete={() => resetProjectFilter()}
							/>
						</If>

						<If condition={currentFilters.locationId}>
							<Chip
								label={`Location: ${(records.locations.get && records.locations.get(currentFilters.locationId)?.label) || ""}`}
								onDelete={() =>
									setCurrentFilters({ ...currentFilters, locationId: null })
								}
							/>
						</If>
						<If
							condition={
								currentFilters.officerId &&
								selectedOfficer &&
								activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key
							}
						>
							<Chip
								label={`Officer: ${selectedOfficer?.ContactName || ""}`}
								onDelete={() =>
									setCurrentFilters({ ...currentFilters, officerId: null })
								}
							/>
						</If>
						<If condition={currentFilters.startDate}>
							<Chip
								label={`Job Date: ${utils.formatDate(currentFilters.startDate, "en-US", undefined, "numeric")} - ${utils.formatDate(currentFilters.endDate, "en-US", undefined, "numeric")}`}
								onDelete={() =>
									setCurrentFilters({
										...currentFilters,
										startDate: null,
										endDate: null,
									})
								}
							/>
						</If>

						<If
							condition={
								currentFilters.startCreateDate &&
								currentFilters.endCreateDate &&
								activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key
							}
						>
							<Chip
								label={`Created Date: ${utils.formatDate(currentFilters.startCreateDate, "en-US", undefined, "numeric")} - ${utils.formatDate(currentFilters.endCreateDate, "en-US", undefined, "numeric")}`}
								onDelete={() =>
									setCurrentFilters({
										...currentFilters,
										startCreateDate: null,
										endCreateDate: null,
									})
								}
							/>
						</If>

						<If
							condition={
								currentFilters.supervisorIds &&
								currentFilters.supervisorIds.length > 0
							}
						>
							<For
								each="coordinator"
								of={selectedCoordinators}
							>
								<Chip
									key={coordinator.value}
									label={`Coordinator: ${coordinator.label}`}
									onDelete={() =>
										resetCoordinatorFilterMultiselect(coordinator.value)
									}
								/>
							</For>
						</If>

						<If
							condition={
								currentFilters.paid !== null &&
								activeType === NON_RKPAY_INVOICE_TYPE.ESTIMATES.key
							}
						>
							<Chip
								label={`Paid Status: ${paidFilterOptions.find(po => po.value === currentFilters.paid)?.label || ""}`}
								onDelete={() =>
									setCurrentFilters({ ...currentFilters, paid: null })
								}
							/>
						</If>

						<If
							condition={
								currentFilters.invoiceType !== null &&
								activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key
							}
						>
							<Chip
								label={`Invoice Type: ${invoiceTypeOptions.find(ito => ito.value === parseInt(currentFilters.invoiceType, 10))?.label || ""}`}
								onDelete={() =>
									setCurrentFilters({ ...currentFilters, invoiceType: null })
								}
							/>
						</If>

						<If condition={showClearAllFilters()}>
							<span
								className="clear-all__filters"
								onClick={clearAllFilters}
							>
								{lang.t("Clear All")}
							</span>
						</If>
					</div>

					<div
						className={`col-xs-12 flex flex-wrap flex-gap-20 margin-top-20 toogle-container ${showFilters ? "" : "hidden-xs"}`}
					>
						<If condition={activeType !== NON_RKPAY_INVOICE_TYPE.ESTIMATES.key}>
							<Toogle
								text={lang.t("Vehicle Invoicing Required")}
								checked={currentFilters.vehicleInvoicingRequired}
								onChecked={vehicleInvoicingRequired =>
									setCurrentFilters({
										...currentFilters,
										vehicleInvoicingRequired,
									})
								}
							/>
						</If>
						<If
							condition={
								isAdminOrOfficerInvoicingV3 === true &&
								activeType === NON_RKPAY_INVOICE_TYPE.PENDING_SUBMISSION.key
							}
						>
							<Toogle
								text={`${lang.t("Pending Officer Submission")} (${pendingSubmissionByOfficerCount})`}
								checked={currentFilters.pendingOfficerSubmission}
								onChecked={pendingOfficerSubmission =>
									setCurrentFilters({
										...currentFilters,
										pendingOfficerSubmission,
									})
								}
							/>
						</If>
						{clientV4InvoicingDisputes
							&& activeType === NON_RKPAY_INVOICE_TYPE.PENDING_SUBMISSION.key
							&& (
								<Toogle
									text={lang.t("Only Disputed")}
									checked={currentFilters.disputed}
									onChecked={disputed =>
										setCurrentFilters({
											...currentFilters,
											disputed: disputed || null,
										})
									}
								/>
							)}
						<If condition={activeType === NON_RKPAY_INVOICE_TYPE.PAID.key}>
							<Toogle
								text={`${lang.t("Overpaid Invoices")} (${overpaidInvoicesCount})`}
								checked={currentFilters.onlyOverpaid}
								onChecked={onlyOverpaid =>
									setCurrentFilters({ ...currentFilters, onlyOverpaid })
								}
							/>
						</If>
						<If condition={activeType === NON_RKPAY_INVOICE_TYPE.ESTIMATES.key || activeType === NON_RKPAY_INVOICE_TYPE.PAID.key}>
							<Toogle
								text={lang.t("Only Archived")}
								checked={currentFilters.archived}
								onChecked={archived =>
									setCurrentFilters({ ...currentFilters, archived })
								}
							/>
						</If>
						{/** Uncomment this code once ready to use these filters
						<Toogle
							text={lang.t('Prepaid')}
							checked={currentFilters.prepaid}
							onChecked={(prepaid) => setCurrentFilters({ ...currentFilters, prepaid })}
						/>
						*/}
						<If condition={onlyVoidedToggleEnabled && activeType !== NON_RKPAY_INVOICE_TYPE.PAID.key && activeType !== NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key}>
							<Toogle
								text={lang.t('Only Voided')}
								checked={currentFilters.onlyVoided}
								onChecked={(onlyVoided) => setCurrentFilters({ ...currentFilters, onlyVoided })}
							/>
						</If>
					</div>
				</div>
			</div>

			<DateRange
				show={showCalendar}
				onCancel={() => setShowCalendar(false)}
				onChange={({ startDate, endDate }) =>
					setCurrentFilters({ ...currentFilters, startDate, endDate })
				}
			/>
			<DateRange
				show={showCreateDateCalendar}
				onCancel={() => setShowCreateDateCalendar(false)}
				onChange={({ startDate, endDate }) =>
					setCurrentFilters({
						...currentFilters,
						startCreateDate: startDate,
						endCreateDate: endDate,
					})
				}
			/>
			<SearchOfficerModal
				show={showSearchOfficerModal}
				user={user}
				closeModal={() => setShowSearchOfficerModal(false)}
				callback={handleOfficerChange}
			/>
		</Fragment>
	);
};

NonRKPayInvoiceFilters.propTypes = {
	user: PropTypes.object,
	activeFilters: PropTypes.object.isRequired,
	activeType: PropTypes.string.isRequired,
	onFiltersChange: PropTypes.func.isRequired,
	overpaidInvoicesCount: PropTypes.number,
	pendingSubmissionByOfficerCount: PropTypes.number,
};

NonRKPayInvoiceFilters.defaultProps = {
	user: {},
	overpaidInvoicesCount: 0,
	pendingSubmissionByOfficerCount: 0,
};

export default NonRKPayInvoiceFilters;
